import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { squadService, friendshipService } from '../../../services/socialService';
import { useSquadMembersRealtime } from '../../../hooks/useSocialRealtime';
import { supabase } from '../../../lib/supabase';
import LaunchChallengeModal from './LaunchChallengeModal';

// ── Invite Member Modal ──────────────────────────────────────────────────────
const InviteMemberModal = ({ isOpen, onClose, squadId, userId, currentMembers, onInvited }) => {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) fetchFriends();
  }, [isOpen]);

  const fetchFriends = async () => {
    setLoading(true);
    try {
      const data = await friendshipService?.getFriends(userId);
      const memberIds = new Set(currentMembers?.map(m => m?.user_id));
      setFriends((data || [])?.filter(f => !memberIds?.has(f?.friend_id)));
    } catch (e) {
      console.error('Error fetching friends:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (friendId) => {
    setInviting(friendId);
    try {
      await squadService?.createSquadInvitation(squadId, userId, friendId);
      onInvited?.();
    } catch (e) {
      console.error('Error inviting member:', e);
    } finally {
      setInviting(null);
    }
  };

  const filtered = friends?.filter(f =>
    f?.user_profiles?.full_name?.toLowerCase()?.includes(search?.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative w-full max-w-md bg-card rounded-xl shadow-xl border border-border max-h-[80vh] flex flex-col"
      >
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Invite Member</h3>
            <p className="text-xs text-muted-foreground">Invite friends to join this squad</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>
        <div className="p-4 border-b border-border">
          <Input
            placeholder="Search friends..."
            value={search}
            onChange={e => setSearch(e?.target?.value)}
            className="w-full"
          />
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-2">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filtered?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Users" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                {friends?.length === 0 ? 'No friends available to invite' : 'No friends match your search'}
              </p>
            </div>
          ) : (
            filtered?.map(f => (
              <div key={f?.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 hover:bg-muted/40 transition-colors">
                <div className="flex items-center gap-3">
                  {f?.user_profiles?.avatar_url ? (
                    <img src={f?.user_profiles?.avatar_url} alt={f?.user_profiles?.full_name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary">
                        {f?.user_profiles?.full_name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-foreground text-sm">{f?.user_profiles?.full_name}</p>
                    <p className="text-xs text-muted-foreground">{f?.user_profiles?.total_points || 0} pts · {f?.user_profiles?.current_streak || 0} 🔥</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleInvite(f?.friend_id)}
                  loading={inviting === f?.friend_id}
                  disabled={!!inviting}
                  iconName="UserPlus"
                >
                  Invite
                </Button>
              </div>
            ))
          )}
        </div>
      </motion.div>
    </div>
  );
};

// ── Squad Stats Card ─────────────────────────────────────────────────────────
const SquadStatsPanel = ({ members, squad }) => {
  const totalPoints = members?.reduce((s, m) => s + (m?.user?.total_points || 0), 0);
  const avgStreak = members?.length > 0
    ? Math.round(members?.reduce((s, m) => s + (m?.user?.current_streak || 0), 0) / members?.length)
    : 0;
  const topMember = [...(members || [])]?.sort((a, b) => (b?.user?.total_points || 0) - (a?.user?.total_points || 0))?.[0];

  const stats = [
    { label: 'Total Points', value: totalPoints?.toLocaleString(), icon: 'Star', color: 'text-primary', bg: 'bg-primary/10' },
    { label: 'Avg Streak', value: `${avgStreak} 🔥`, icon: 'Flame', color: 'text-warning', bg: 'bg-warning/10' },
    { label: 'Members', value: members?.length || 0, icon: 'Users', color: 'text-success', bg: 'bg-success/10' },
    { label: 'Top Scorer', value: topMember?.user?.full_name?.split(' ')?.[0] || '—', icon: 'Trophy', color: 'text-accent', bg: 'bg-accent/10' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
      {stats?.map((stat, i) => (
        <motion.div
          key={stat?.label}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.07 }}
          className="p-4 bg-card rounded-xl border border-border text-center"
        >
          <div className={`w-9 h-9 rounded-lg ${stat?.bg} flex items-center justify-center mx-auto mb-2`}>
            <Icon name={stat?.icon} size={18} className={stat?.color} />
          </div>
          <p className={`text-lg font-bold ${stat?.color}`}>{stat?.value}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{stat?.label}</p>
        </motion.div>
      ))}
    </div>
  );
};

// ── Member Row ───────────────────────────────────────────────────────────────
const MemberRow = ({ member, isAdmin, currentUserId, onRemove, removing }) => {
  const isCurrentUser = member?.user_id === currentUserId;
  const isMemberAdmin = member?.role === 'admin';

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className="flex items-center justify-between p-3 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors"
    >
      <div className="flex items-center gap-3">
        {member?.user?.avatar_url ? (
          <img src={member?.user?.avatar_url} alt={member?.user?.full_name} className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-sm font-semibold text-primary">
              {member?.user?.full_name?.charAt(0)?.toUpperCase()}
            </span>
          </div>
        )}
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-foreground text-sm">{member?.user?.full_name}</p>
            {isMemberAdmin && (
              <span className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded-full font-medium">Admin</span>
            )}
            {isCurrentUser && (
              <span className="text-xs px-1.5 py-0.5 bg-muted text-muted-foreground rounded-full">You</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {(member?.user?.total_points || 0)?.toLocaleString()} pts · {member?.user?.current_streak || 0} 🔥
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="text-right hidden sm:block">
          <p className="text-sm font-semibold text-primary">{(member?.user?.total_points || 0)?.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">points</p>
        </div>
        {isAdmin && !isCurrentUser && !isMemberAdmin && (
          <button
            onClick={() => onRemove(member)}
            disabled={removing === member?.user_id}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-error hover:bg-error/10 transition-colors disabled:opacity-50"
            title="Remove member"
          >
            {removing === member?.user_id ? (
              <div className="w-4 h-4 border-2 border-error/50 border-t-error rounded-full animate-spin" />
            ) : (
              <Icon name="UserMinus" size={16} />
            )}
          </button>
        )}
      </div>
    </motion.div>
  );
};

// ── Shared Challenges Panel ──────────────────────────────────────────────────
const SharedChallengesPanel = ({ squadId, userId, challenges, onJoinChallenge }) => {
  const [squadChallenges, setSquadChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joiningId, setJoiningId] = useState(null);
  const [joinedIds, setJoinedIds] = useState(new Set());
  const [confirmedIds, setConfirmedIds] = useState(new Set());

  useEffect(() => {
    fetchSquadChallenges();
  }, [squadId]);

  const fetchSquadChallenges = async () => {
    setLoading(true);
    try {
      // Get challenge participants for this squad's members
      const { data: members } = await supabase
        ?.from('squad_members')
        ?.select('user_id')
        ?.eq('squad_id', squadId);

      if (!members?.length) { setSquadChallenges([]); setLoading(false); return; }

      const memberIds = members?.map(m => m?.user_id);

      const { data: participations } = await supabase
        ?.from('challenge_participants')
        ?.select(`
          user_id,
          current_progress,
          is_completed,
          wellness_challenges(id, title, description, challenge_type, target_value, unit, icon, color, end_date)
        `)
        ?.in('user_id', memberIds)
        ?.order('joined_at', { ascending: false });

      // Group by challenge
      const challengeMap = {};
      (participations || [])?.forEach(p => {
        const ch = p?.wellness_challenges;
        if (!ch) return;
        if (!challengeMap?.[ch?.id]) {
          challengeMap[ch?.id] = { ...ch, participants: [] };
        }
        challengeMap?.[ch?.id]?.participants?.push({
          userId: p?.user_id,
          progress: p?.current_progress,
          isCompleted: p?.is_completed,
        });
      });

      setSquadChallenges(Object.values(challengeMap));
    } catch (e) {
      console.error('Error fetching squad challenges:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (challengeId) => {
    if (joiningId || joinedIds?.has(challengeId)) return;
    setJoiningId(challengeId);
    try {
      // Optimistically add user as participant
      setSquadChallenges(prev =>
        prev?.map(ch =>
          ch?.id === challengeId
            ? { ...ch, participants: [...(ch?.participants || []), { userId, progress: 0, isCompleted: false }] }
            : ch
        )
      );
      setJoinedIds(prev => new Set([...prev, challengeId]));

      // Call parent handler or insert directly
      if (onJoinChallenge) {
        await onJoinChallenge?.(challengeId);
      } else {
        const { error } = await supabase
          ?.from('challenge_participants')
          ?.insert({ challenge_id: challengeId, user_id: userId, current_progress: 0, is_completed: false });
        if (error) throw error;
      }

      // Show confirmation feedback
      setConfirmedIds(prev => new Set([...prev, challengeId]));
      setTimeout(() => {
        setConfirmedIds(prev => {
          const next = new Set(prev);
          next?.delete(challengeId);
          return next;
        });
      }, 3000);
    } catch (e) {
      console.error('Error joining challenge:', e);
      // Rollback optimistic update on error
      setJoinedIds(prev => {
        const next = new Set(prev);
        next?.delete(challengeId);
        return next;
      });
      setSquadChallenges(prev =>
        prev?.map(ch =>
          ch?.id === challengeId
            ? { ...ch, participants: ch?.participants?.filter(p => p?.userId !== userId) }
            : ch
        )
      );
    } finally {
      setJoiningId(null);
    }
  };

  const colorMap = {
    success: { bg: 'bg-success/10', text: 'text-success', bar: 'bg-success', border: 'border-success/30' },
    accent: { bg: 'bg-accent/10', text: 'text-accent', bar: 'bg-accent', border: 'border-accent/30' },
    warning: { bg: 'bg-warning/10', text: 'text-warning', bar: 'bg-warning', border: 'border-warning/30' },
    primary: { bg: 'bg-primary/10', text: 'text-primary', bar: 'bg-primary', border: 'border-primary/30' },
    error: { bg: 'bg-error/10', text: 'text-error', bar: 'bg-error', border: 'border-error/30' },
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (squadChallenges?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 bg-muted/20 rounded-xl border border-dashed border-border">
        <Icon name="Trophy" size={36} className="text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground text-center">No shared challenges yet</p>
        <p className="text-xs text-muted-foreground text-center mt-1">Join a challenge to see squad progress here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {squadChallenges?.map((ch, i) => {
        const colors = colorMap?.[ch?.color] || colorMap?.primary;
        const daysLeft = Math.ceil((new Date(ch?.end_date) - new Date()) / (1000 * 60 * 60 * 24));
        const completedCount = ch?.participants?.filter(p => p?.isCompleted)?.length || 0;
        const userPart = ch?.participants?.find(p => p?.userId === userId);
        const isJoining = joiningId === ch?.id;
        const hasJoined = !!userPart;
        const justConfirmed = confirmedIds?.has(ch?.id);
        const avgProgress = ch?.participants?.length > 0
          ? Math.round(ch?.participants?.reduce((s, p) => s + (p?.progress || 0), 0) / ch?.participants?.length)
          : 0;
        const progressPct = ch?.target_value > 0 ? Math.min((avgProgress / ch?.target_value) * 100, 100) : 0;

        return (
          <motion.div
            key={ch?.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className={`p-4 rounded-xl border ${
              justConfirmed ? 'border-success/50 bg-success/5' : `${colors?.bg} border-border`
            } transition-colors duration-500`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${colors?.bg} border border-border flex items-center justify-center relative`}>
                  <Icon name={ch?.icon || 'Trophy'} size={20} className={colors?.text} />
                  {hasJoined && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-success rounded-full flex items-center justify-center">
                      <Icon name="Check" size={10} className="text-white" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-foreground text-sm">{ch?.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {ch?.participants?.length} squad members · {daysLeft > 0 ? `${daysLeft}d left` : 'Ended'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-sm font-bold ${colors?.text}`}>{completedCount}/{ch?.participants?.length}</p>
                <p className="text-xs text-muted-foreground">completed</p>
              </div>
            </div>
            {/* Squad avg progress bar */}
            <div className="mb-3">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Squad Avg Progress</span>
                <span className={colors?.text}>{progressPct?.toFixed(0)}%</span>
              </div>
              <div className="w-full bg-muted/40 rounded-full h-2">
                <motion.div
                  className={`h-2 rounded-full ${colors?.bar}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 0.8, delay: i * 0.1 }}
                />
              </div>
            </div>
            {/* Member progress dots */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {ch?.participants?.slice(0, 8)?.map((p, pi) => {
                const pct = ch?.target_value > 0 ? Math.min((p?.progress / ch?.target_value) * 100, 100) : 0;
                const isCurrentUser = p?.userId === userId;
                return (
                  <div
                    key={pi}
                    title={`${isCurrentUser ? 'You' : 'Member'} · ${pct?.toFixed(0)}% progress`}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs transition-all ${
                      p?.isCompleted
                        ? `${colors?.bar} border-transparent text-white`
                        : isCurrentUser
                        ? `bg-primary/20 border-primary text-primary font-bold`
                        : 'bg-muted border-border text-muted-foreground'
                    }`}
                  >
                    {p?.isCompleted ? '✓' : isCurrentUser ? 'Y' : ''}
                  </div>
                );
              })}
              {ch?.participants?.length > 8 && (
                <span className="text-xs text-muted-foreground">+{ch?.participants?.length - 8}</span>
              )}
            </div>
            {/* Join / Joined state */}
            <AnimatePresence mode="wait">
              {!hasJoined ? (
                <motion.div
                  key="join-btn"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="mt-3"
                >
                  <button
                    onClick={() => handleJoin(ch?.id)}
                    disabled={isJoining || daysLeft <= 0}
                    className={`w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium border transition-all ${
                      daysLeft <= 0
                        ? 'opacity-40 cursor-not-allowed bg-muted text-muted-foreground border-border'
                        : `${colors?.bg} ${colors?.text} border-current hover:opacity-80 active:scale-95`
                    }`}
                  >
                    {isJoining ? (
                      <>
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        <span>Joining...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Plus" size={15} />
                        <span>{daysLeft <= 0 ? 'Challenge Ended' : 'Join Challenge'}</span>
                      </>
                    )}
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="joined-state"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="mt-3"
                >
                  {justConfirmed ? (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-success/10 border border-success/30 text-success text-sm font-medium"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                      >
                        <Icon name="CheckCircle" size={16} />
                      </motion.div>
                      <span>You've joined this challenge!</span>
                    </motion.div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-muted/30 border border-border text-muted-foreground text-sm">
                      <Icon name="Check" size={14} className="text-success" />
                      <span>Already participating</span>
                      {userPart?.isCompleted && (
                        <span className="ml-1 text-xs px-1.5 py-0.5 bg-success/10 text-success rounded-full font-medium">Completed ✓</span>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};

// ── Main SquadScreen ─────────────────────────────────────────────────────────
const SquadScreen = ({ squads, userId, onSquadCreated, onUpdate, challenges, onJoinChallenge }) => {
  const [selectedSquadId, setSelectedSquadId] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [activeTab, setActiveTab] = useState('roster'); // 'roster' | 'challenges'
  const [confirmRemove, setConfirmRemove] = useState(null);

  const selectedSquad = squads?.find(s => s?.id === selectedSquadId);
  const isAdmin = members?.find(m => m?.user_id === userId)?.role === 'admin';

  // Auto-select first squad
  useEffect(() => {
    if (squads?.length > 0 && !selectedSquadId) {
      setSelectedSquadId(squads?.[0]?.id);
    }
  }, [squads]);

  // Fetch members when squad changes
  useEffect(() => {
    if (selectedSquadId) fetchMembers();
  }, [selectedSquadId]);

  // Real-time member updates
  useSquadMembersRealtime(selectedSquadId, (payload) => {
    if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE' || payload?.eventType === 'DELETE') {
      fetchMembers();
    }
  });

  const fetchMembers = useCallback(async () => {
    if (!selectedSquadId) return;
    setMembersLoading(true);
    try {
      const data = await squadService?.getSquadMembers(selectedSquadId);
      setMembers(data || []);
    } catch (e) {
      console.error('Error fetching members:', e);
    } finally {
      setMembersLoading(false);
    }
  }, [selectedSquadId]);

  const handleRemoveMember = async (member) => {
    setRemoving(member?.user_id);
    setConfirmRemove(null);
    try {
      const { error } = await supabase
        ?.from('squad_members')
        ?.delete()
        ?.eq('squad_id', selectedSquadId)
        ?.eq('user_id', member?.user_id);
      if (error) throw error;
      setMembers(prev => prev?.filter(m => m?.user_id !== member?.user_id));
      onUpdate?.();
    } catch (e) {
      console.error('Error removing member:', e);
    } finally {
      setRemoving(null);
    }
  };

  const sortedMembers = [...(members || [])]?.sort((a, b) => {
    if (a?.role === 'admin' && b?.role !== 'admin') return -1;
    if (b?.role === 'admin' && a?.role !== 'admin') return 1;
    return (b?.user?.total_points || 0) - (a?.user?.total_points || 0);
  });

  if (squads?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
          <Icon name="Users" size={36} className="text-primary" />
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">No Squads Yet</h3>
        <p className="text-muted-foreground text-sm max-w-xs mb-6">
          Create your first squad to compete together, share challenges, and track group progress.
        </p>
        <Button onClick={onSquadCreated} iconName="Plus" size="lg">
          Create Your First Squad
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Squad Selector */}
      <div className="flex gap-2 flex-wrap">
        {squads?.map(squad => (
          <button
            key={squad?.id}
            onClick={() => { setSelectedSquadId(squad?.id); setActiveTab('roster'); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border ${
              selectedSquadId === squad?.id
                ? 'bg-primary text-primary-foreground border-primary shadow-md'
                : 'bg-card text-foreground border-border hover:border-primary/50'
            }`}
          >
            <Icon name={squad?.icon || 'Users'} size={15} />
            {squad?.name}
          </button>
        ))}
      </div>
      {selectedSquad && (
        <motion.div
          key={selectedSquad?.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-5"
        >
          {/* Squad Header */}
          <div className="flex items-start justify-between p-5 bg-card rounded-xl border border-border">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name={selectedSquad?.icon || 'Users'} size={28} className="text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">{selectedSquad?.name}</h2>
                {selectedSquad?.description && (
                  <p className="text-sm text-muted-foreground mt-0.5">{selectedSquad?.description}</p>
                )}
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Icon name="Users" size={12} />
                    {members?.length} members
                  </span>
                  {isAdmin && (
                    <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full font-medium">Admin</span>
                  )}
                </div>
              </div>
            </div>
            {isAdmin && (
              <Button
                size="sm"
                iconName="UserPlus"
                onClick={() => setShowInviteModal(true)}
              >
                Invite
              </Button>
            )}
          </div>

          {/* Stats */}
          {!membersLoading && <SquadStatsPanel members={members} squad={selectedSquad} />}

          {/* Tab Navigation */}
          <div className="flex gap-1 p-1 bg-muted/30 rounded-xl border border-border w-fit">
            {[{ id: 'roster', label: 'Member Roster', icon: 'Users' }, { id: 'challenges', label: 'Shared Challenges', icon: 'Trophy' }]?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab?.id
                    ? 'bg-card text-foreground shadow-sm border border-border'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={15} />
                {tab?.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {activeTab === 'roster' && (
              <motion.div
                key="roster"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="bg-card rounded-xl border border-border overflow-hidden"
              >
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Icon name="Users" size={16} className="text-primary" />
                    <h3 className="font-semibold text-foreground">Members ({members?.length})</h3>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => setShowInviteModal(true)}
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      <Icon name="UserPlus" size={13} /> Invite
                    </button>
                  )}
                </div>

                <div className="p-4 space-y-2">
                  {membersLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                    </div>
                  ) : sortedMembers?.length === 0 ? (
                    <div className="text-center py-8">
                      <Icon name="Users" size={32} className="text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No members yet</p>
                    </div>
                  ) : (
                    sortedMembers?.map(member => (
                      <MemberRow
                        key={member?.id}
                        member={member}
                        isAdmin={isAdmin}
                        currentUserId={userId}
                        onRemove={(m) => setConfirmRemove(m)}
                        removing={removing}
                      />
                    ))
                  )}
                </div>

                {/* Leaderboard strip */}
                {sortedMembers?.length > 1 && (
                  <div className="border-t border-border p-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Points Leaderboard</p>
                    <div className="space-y-2">
                      {sortedMembers?.slice(0, 5)?.map((member, idx) => {
                        const maxPts = sortedMembers?.[0]?.user?.total_points || 1;
                        const pct = Math.min(((member?.user?.total_points || 0) / maxPts) * 100, 100);
                        const medals = ['🥇', '🥈', '🥉'];
                        return (
                          <div key={member?.id} className="flex items-center gap-3">
                            <span className="text-sm w-5 text-center">{medals?.[idx] || `${idx + 1}`}</span>
                            <div className="flex-1">
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-foreground font-medium">{member?.user?.full_name?.split(' ')?.[0]}</span>
                                <span className="text-muted-foreground">{(member?.user?.total_points || 0)?.toLocaleString()} pts</span>
                              </div>
                              <div className="w-full bg-muted/40 rounded-full h-1.5">
                                <div
                                  className="h-1.5 rounded-full bg-primary transition-all duration-700"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'challenges' && (
              <motion.div
                key="challenges"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
              >
                {isAdmin && (
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Icon name="Trophy" size={16} className="text-primary" />
                      <h3 className="font-semibold text-foreground text-sm">Shared Challenges</h3>
                    </div>
                    <Button
                      size="sm"
                      iconName="Rocket"
                      onClick={() => setShowLaunchModal(true)}
                    >
                      Launch Challenge
                    </Button>
                  </div>
                )}
                <SharedChallengesPanel
                  squadId={selectedSquadId}
                  userId={userId}
                  challenges={challenges}
                  onJoinChallenge={onJoinChallenge}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
      {/* Launch Challenge Modal */}
      <AnimatePresence>
        {showLaunchModal && (
          <LaunchChallengeModal
            isOpen={showLaunchModal}
            onClose={() => setShowLaunchModal(false)}
            squadId={selectedSquadId}
            userId={userId}
            squadName={selectedSquad?.name}
            onLaunched={() => {
              setShowLaunchModal(false);
              onUpdate?.();
            }}
          />
        )}
      </AnimatePresence>
      {/* Invite Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <InviteMemberModal
            isOpen={showInviteModal}
            onClose={() => setShowInviteModal(false)}
            squadId={selectedSquadId}
            userId={userId}
            currentMembers={members}
            onInvited={() => {
              setShowInviteModal(false);
              fetchMembers();
            }}
          />
        )}
      </AnimatePresence>
      {/* Confirm Remove Dialog */}
      <AnimatePresence>
        {confirmRemove && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmRemove(null)} />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-sm bg-card rounded-xl shadow-xl border border-border p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center">
                  <Icon name="UserMinus" size={20} className="text-error" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Remove Member</h3>
                  <p className="text-xs text-muted-foreground">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                Are you sure you want to remove <span className="font-medium text-foreground">{confirmRemove?.user?.full_name}</span> from the squad?
              </p>
              <div className="flex gap-3">
                <Button variant="outline" fullWidth onClick={() => setConfirmRemove(null)}>Cancel</Button>
                <Button
                  variant="destructive"
                  fullWidth
                  onClick={() => handleRemoveMember(confirmRemove)}
                  iconName="UserMinus"
                >
                  Remove
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SquadScreen;
